import React, { useState, useEffect, useRef } from 'react';
import { fetchStatusTypes } from 'store/offers/actions';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import BasicFormContainer from 'components/layout/BasicFormContainer';
import IOfferGetSingle from 'api/interfaces/offers/IOfferGetSingle';
import IOfferPost from 'api/interfaces/offers/IOfferPost';
import IAdditionalOfferPost from 'api/interfaces/offers/IAdditionalOfferPost';
import IAdditionalOfferGet from 'api/interfaces/offers/IAdditionalOfferGet';
import PageContainer from 'components/layout/PageContainer';
import PrimaryButton from 'components/layout/buttons/PrimaryButton';
import getDateFromISO from 'services/dates/getDateISO';
import getISODate from 'services/dates/getISODate';
import getOffersDropdown from 'services/offers/getOffersDropdown';
import getTargetValue from 'services/forms/getTargetValue';
import translations from 'services/translations/translations';
import { useFeedbackSection } from 'services/feedback/feedbackSection';
import getCustomerDropdown from 'services/offers/getCustomerDropdown';

import IUserGet from 'api/interfaces/users/IUserGet';
import {
  ADDITIONAL_OFFER,
  MATERIAL_OFFER,
  OFFER,
  OPPORTUNITY,
  PRELIMINARY_OFFER,
} from 'services/enums/offers/offerTypes';
import {
  APPROVED,
  CONVERTED_TO_OFFER,
  OPEN,
  SENT,
  isClosedOpportunityStatus,
  isConvertedStatus,
} from 'services/enums/offers/Statuses';
import LinkButton from 'components/layout/buttons/LinkButton';
import { DELETE_OFFERS } from 'services/enums/users/permissions';
import { addMessage } from 'store/messages/actions';
import { LEFT, RIGHT } from 'services/enums/sides';
import { removeNavMenuText, setNavMenuText } from 'store/navMenu/actions';

import IOfferProductsGet from 'api/interfaces/offers/IOfferProductsGet';
import IOfferNda from 'api/interfaces/offers/IOfferNda';
import {
  POSITIONS,
  isNdaRestricted,
} from 'services/enums/offers/ndaProtections';
import { NO } from 'services/enums/offers/collateralTypes';
import AdditionalOfferBasicInfo from './src/offer/AdditionalOfferBasicInfo';
import additionalOfferRetrieved from './additionalOfferRetrieved';
import apiOffers from '../../api/offers';
import customerApi from '../../api/contacts';
import getDefaultOffer from './src/offer/getDefaultOffer';
import getOfferType from './getOfferType';
import IOffer from './src/offer/IOffer';
import OfferProducts from './src/offer/OfferProducts';
import OfferBasicInfo from './src/offer/OfferBasicInfo';
import CollapsibleOfferSection from './src/offer/CollapsibleOfferSection';
import OfferExtraPages from './src/offer/OfferExtraPages';
import OfferPriceList from './src/offer/OfferPriceList';
import OpportunityEstimates, {
  getEstimateRows,
  getEstimatesPost,
  IEstimateRow,
} from './src/offer/OpportunityEstimates';
import ProjectContacts, {
  getProjectContactRows,
  getProjectContactsPost,
  IProjectContactRow,
} from './src/offer/ProjectContacts';
import OfferDepartment from './src/offer/OfferDepartment';
import OfferWorkNumbers from './src/offer/OfferWorkNumbers';
import OfferVisitors from './src/offer/OfferVisitors';
import OfferHistoryModal from './src/offer/OfferHistoryModal';
import getConflictMessage from '../../services/offers/getConflictMessage';
import useOfferVisit from '../../services/offers/useOfferVisit';
import IOfferWorkNumbers from '../../api/interfaces/offers/IOfferWorkNumbers';
import OfferTextField from './src/offer/OfferTextField';
import OfferMemo from './src/offer/OfferMemo';
import OfferNdaModal from './src/offer/OfferNdaModal';
import OfferRevisions from './src/offer/OfferRevisions';
import AdditionalRecipients, {
  IRecipientRow,
  getRecipientRows,
} from './src/offer/AdditionalRecipients';
import PdfPreviewPanel from './src/pdfPreview/PdfPreviewPanel';
import openOfferPdf from './services/openOfferPdf';
import { getOfferPost, getFormState } from './src/offer/OfferUtils';

import 'style/offers/Offer.css';
import 'style/offers/OfferNda.css';
import ModalConfirm from '../layout/modals/ModalConfirm';

interface IRouteParams {
  type: string;
  typeId: string;
}

const Offer = (): JSX.Element | null => {
  const { t } = useTranslation([translations.offers, translations.common]);
  const { id }: { id: string | undefined } = useParams();
  const { typeId, type } = useParams<IRouteParams>();
  const dispatch = useDispatch();
  const history = useHistory();
  const [products, setProducts] = useState<IOfferProductsGet[] | null>(null);

  const [formState, setFormState] = useState<IOffer>(getDefaultOffer());
  // Feedback given on the page is about the summary of the offer
  useFeedbackSection('Tarjous yhteenveto');
  const [customerOffice, setCustomerOffice] = useState<{
    label: string;
    value: string;
  }>();
  // An opportunity: the estimated sums by work type
  const [estimateRows, setEstimateRows] = useState<IEstimateRow[]>([]);
  // The offer made from this opportunity, and the opportunity this offer was made from
  const [convertedOfferId, setConvertedOfferId] = useState<null | number>(null);
  const [opportunityId, setOpportunityId] = useState<null | number>(null);
  // The contacts of the project besides the customers, e.g. the architects
  const [projectContactRows, setProjectContactRows] = useState<
    IProjectContactRow[]
  >([]);
  const paymentTerms = [0, 7, 14, 21, 30, 45, 60, 90];
  const [paymentTerm, setPaymentTerm] = useState<{
    label: string;
    value: string;
  }>();
  const [offerIdConnected, setOfferIdConnected] = useState<{
    label: string;
    value: string;
  }>();
  const [signatureUser, setSignatureUser] = useState<{
    label: string;
    value: string;
  }>();
  const [startDate, setStartDate] = useState<Date | null>(new Date());
  const [endDate, setEndDate] = useState<Date | null>(new Date());
  // The start and end of the whole building project, empty when not known
  const [siteStartDate, setSiteStartDate] = useState<Date | null>(null);
  const [siteEndDate, setSiteEndDate] = useState<Date | null>(null);
  // The date the customer wishes to have the offer by, empty when not given
  const [requestedSubmissionDate, setRequestedSubmissionDate] =
    useState<Date | null>(null);
  const [offerIsOpen, setOfferIsOpen] = useState(true);
  const [initialStatus, setInitialStatus] = useState<null | string>(null);
  const [locked, setLocked] = useState(false);
  // The user can only read the offers of the department, nothing can be changed
  const [readOnly, setReadOnly] = useState(false);
  // The work numbers from ProBuilder once ordered
  const [workNumbers, setWorkNumbers] = useState<IOfferWorkNumbers | null>(
    null
  );
  // The edit version of the loaded offer: a save is refused if another user saved after it
  const [editVersion, setEditVersion] = useState<number>();
  // A save is on its way to the server
  const saving = useRef(false);
  // The maker of the offer is told when others open it
  const [makerId, setMakerId] = useState<string>();
  // Who opened and saved the offer: for its maker and the log readers
  const [canReadHistory, setCanReadHistory] = useState(false);
  const [historyIsOpen, setHistoryIsOpen] = useState(false);
  const [customers, setCustomers] = useState<string[]>();
  const [productsIsOpen, setProductsIsOpen] = useState(false);
  const [deliveryTermsIsOpen, setDeliveryTermsIsOpen] = useState(false);
  const [offerSubmitted, setOfferSubmitted] = useState(false);
  const [savedId, setSavedId] = useState<number>();
  const [additionalOfferFormState, setAdditionalOfferFormState] =
    useState<IAdditionalOfferGet>({
      created: null,
      createdUser: null,
      description: '',
      offerText: '',
      deliveryTerms: '',
      organizationUnit: null,
      signatureUser: null,
      status: '',
      updated: null,
      updatedUser: null,
    });
  const [modalDeleteConfirmation, setModalDeleteConfirmation] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [previewIsOpen, setPreviewIsOpen] = useState(false);
  const [revisionCount, setRevisionCount] = useState(1);
  // Internal memo of the offer, null until the offer is loaded
  const [memo, setMemo] = useState<null | string>(null);
  // The quantities are printed in the additional price list, null until the offer is loaded
  const [priceListShowQuantities, setPriceListShowQuantities] = useState<
    null | boolean
  >(null);
  const [priceListIsOpen, setPriceListIsOpen] = useState(false);
  // NDA of the offer, additional offers share the NDA of their offer
  const [nda, setNda] = useState<IOfferNda | null>(null);
  const [ndaOfferId, setNdaOfferId] = useState<number>();
  const [ndaIsOpen, setNdaIsOpen] = useState(false);
  const [additionalRecipients, setAdditionalRecipients] = useState<
    IRecipientRow[]
  >([]);

  useEffect(() => {
    dispatch(fetchStatusTypes());
  }, []);

  useEffect(() => {
    dispatch(setNavMenuText(formState.target, '/offers'));
  }, [formState]);

  useEffect(() => {
    // This is triggered when component is closed
    return () => {
      dispatch(removeNavMenuText());
    };
  }, []);

  const statusTypes = useSelector((state: any) => state.offers.statusTypes);
  const currentUser = useSelector(
    (state: { users: { current: IUserGet | null } }) => state.users.current
  );

  const toggleOffer = () => setOfferIsOpen(!offerIsOpen);
  const toggleProducts = () => setProductsIsOpen(!productsIsOpen);

  // Users outside the NDA cannot open the offer at all when all content is protected
  function showLoadError(error: any) {
    if (error?.response?.status === 403) {
      dispatch(addMessage(t(`${translations.offers}:nda.No access to offer`)));
    }
  }

  useEffect(() => {
    // If not undefined there is an offer to modify
    if (id !== undefined) {
      // A saved offer opens with the basic info closed and the products open, also when
      // moving to another revision of it
      setOfferIsOpen(false);
      setProductsIsOpen(true);
      setOfferSubmitted(true);

      if (type === ADDITIONAL_OFFER) {
        const submit = async () => {
          try {
            const result: IAdditionalOfferGet =
              await apiOffers.additionalOffers.get(parseInt(id, 10));

            if (additionalOfferRetrieved(result)) {
              setAdditionalOfferFormState(result);
              // Set before loading the offer so a failing load never leaves the page editable
              setInitialStatus(result?.status);
              setLocked(result?.isLocked ?? false);
              setReadOnly(result?.readOnly ?? false);
              setWorkNumbers(result?.workNumbers ?? null);
              setEditVersion(result?.editVersion);
              setMakerId(result?.createdUser?.id);
              setCanReadHistory(result?.canReadHistory ?? false);
              setMemo(result?.memo ?? '');
              setPriceListShowQuantities(
                result?.showPriceListQuantities ?? false
              );
              setNda(result?.nda ?? null);
              setNdaOfferId(result?.offerId);

              const connectedOffer: IOfferGetSingle = await apiOffers.getOffer(
                result.offerId
              );

              setOfferIdConnected({
                label: connectedOffer.targetName,
                value: result.offerId?.toString() ?? '',
              });

              if (result.signatureUser !== null) {
                setSignatureUser({
                  label: `${result.signatureUser.firstName} ${result.signatureUser.lastName}`,
                  value: result.signatureUser.id ?? '',
                });
              }
            }
          } catch (error) {
            showLoadError(error);
          }
        };
        submit().catch((e) => console.log(e));
        return;
      }
      const submit = async () => {
        try {
          const result: IOfferGetSingle = await apiOffers.getOffer(
            parseInt(id, 10)
          );

          if (
            result !== undefined &&
            result.address !== undefined &&
            result.invoicingInformation !== undefined &&
            result.customerContacts !== undefined
          ) {
            setFormState(getFormState(formState, result));
            setPaymentTerm({
              label: result?.invoicingInformation.paymentTerm
                ? result?.invoicingInformation.paymentTerm.toString()
                : '',
              value: result?.invoicingInformation.paymentTerm
                ? result?.invoicingInformation.paymentTerm.toString()
                : '',
            });

            setInitialStatus(result?.status);
            setLocked(result.isLocked);
            setReadOnly(result.readOnly ?? false);
            setWorkNumbers(result.workNumbers ?? null);
            setEditVersion(result.editVersion);
            setMakerId(result.createdUser?.id);
            setCanReadHistory(result.canReadHistory ?? false);
            setMemo(result.memo ?? '');
            setPriceListShowQuantities(result.showPriceListQuantities ?? false);
            setNda(result.nda ?? null);
            setNdaOfferId(parseInt(id, 10));

            setStartDate(
              result.workStarts
                ? getDateFromISO(result?.workStarts)
                : new Date()
            );

            setEndDate(
              result.workEnds ? getDateFromISO(result?.workEnds) : new Date()
            );

            setEstimateRows(getEstimateRows(result.estimates));
            setConvertedOfferId(result.convertedOfferId ?? null);
            setOpportunityId(result.opportunityId ?? null);

            setSiteStartDate(
              result.siteStarts ? getDateFromISO(result.siteStarts) : null
            );
            setSiteEndDate(
              result.siteEnds ? getDateFromISO(result.siteEnds) : null
            );
            setRequestedSubmissionDate(
              result.requestedSubmissionDate
                ? getDateFromISO(result.requestedSubmissionDate)
                : null
            );

            if (result.customerContacts.length) {
              setCustomerOffice({
                label: result.customerContacts[0].contact.companyName,
                value: result.customerContacts[0].contact.id.toString(),
              });
              setAdditionalRecipients(
                await getRecipientRows(result.customerContacts.slice(1) as any)
              );
            } else {
              setCustomerOffice({
                label: '',
                value: '',
              });
            }

            // The design office saved earlier is the first contact of the project
            setProjectContactRows(
              getProjectContactRows(
                (result.projectContacts ?? result.designOfficeContacts) as any
              )
            );

            if (result.signatureUser !== null) {
              setSignatureUser({
                label: `${result.signatureUser.firstName} ${result.signatureUser.lastName}`,
                value: result.signatureUser.id ?? '',
              });
            } else {
              setSignatureUser({
                label: '',
                value: '',
              });
            }
          }
        } catch (error) {
          showLoadError(error);
        }
      };
      submit().catch((e) => console.log(e));
    }
  }, [id]);

  // Outside the NDA the offer is read only and protected parts are hidden
  const ndaRestricted = isNdaRestricted(nda);
  // Nothing can be saved, either outside the NDA or with only the right to read
  const viewOnly = ndaRestricted || readOnly;

  const isOfferCreation = () =>
    typeId !== undefined &&
    statusTypes.length !== 0 &&
    formState.status === '' &&
    additionalOfferFormState.status === '';

  const offerId =
    savedId === undefined && id !== undefined ? parseInt(id, 10) : savedId;

  useEffect(() => {
    // If it is offer creation, set the offer type and status

    if (isOfferCreation()) {
      const openStatus = statusTypes.find((x: string) => x === 'open');
      setFormState({
        ...formState,
        offerType: getOfferType(typeId, type),
        status: openStatus || '',
        // The collateral of the department's defaults may have come already
        collateral: formState.collateral || t(`${translations.common}:no`),
        employeeInformationType: t(
          `${translations.common}:client-stamping-device`
        ),
        taxRate: t(`${translations.common}:0`),
      });
      setAdditionalOfferFormState({
        ...additionalOfferFormState,
        status: openStatus || '',
      });
    }
  }, [additionalOfferFormState, formState, statusTypes, typeId]);

  // A new offer or preliminary offer gets the defaults of the user's department (where it
  // is made), only in the fields still empty
  const defaultsApplied = useRef(false);
  useEffect(() => {
    const newType = getOfferType(typeId, type);
    if (
      id !== undefined ||
      defaultsApplied.current ||
      (newType !== OFFER &&
        newType !== PRELIMINARY_OFFER &&
        newType !== MATERIAL_OFFER)
    ) {
      return;
    }

    // Those of the user first, then of the department; a material offer has its own
    defaultsApplied.current = true;
    apiOffers.defaults
      .get({ kind: newType === MATERIAL_OFFER ? 'material-offer' : 'offer' })
      .then((defaults) => {
        setFormState((current) => ({
          ...current,
          attachmentsNote:
            current.attachmentsNote || (defaults.attachmentsNote ?? ''),
          cargo: current.cargo || (defaults.cargo ?? ''),
          deliveryTimeNote:
            current.deliveryTimeNote || (defaults.deliveryTimeNote ?? ''),
          invoiceNote: current.invoiceNote || (defaults.invoiceNote ?? ''),
          validity: current.validity || (defaults.validity ?? ''),
          infoRows:
            (current.infoRows ?? []).length > 0
              ? current.infoRows
              : defaults.infoRows ?? [],
          // "No collateral" is the starting value of a new offer, not a choice yet
          collateral:
            defaults.collateralType &&
            (current.collateral === '' || current.collateral === NO)
              ? defaults.collateralType
              : current.collateral,
          // The offer text chosen from the text archive
          offerText: current.offerText || (defaults.offerText ?? ''),
        }));
        if (defaults.paymentTerm !== null) {
          const term = String(defaults.paymentTerm);
          setPaymentTerm((current) =>
            current?.value ? current : { label: term, value: term }
          );
        }
        if (defaults.signatureUser !== null) {
          const user = defaults.signatureUser;
          setSignatureUser((current) =>
            current?.value
              ? current
              : { label: `${user.firstName} ${user.lastName}`, value: user.id }
          );
        }
      })
      .catch(() => undefined);
  }, [id, typeId, type]);

  useEffect(() => {
    if (customerOffice !== undefined && customerOffice.value !== '') {
      const getContact = async () => {
        const list = await customerApi.getContact(customerOffice.value, {
          ownOrganizationUnit: true,
        });

        setCustomers(list.contactPersons);
      };
      getContact();
    }
  }, [customerOffice]);

  const handleFormState = (e: any) => {
    setFormState({
      ...formState,
      [e.target.name]: getTargetValue(e),
    });
  };

  const handleAdditionalOfferFormState = (e: any) => {
    setAdditionalOfferFormState({
      ...additionalOfferFormState,
      [e.target.name]: getTargetValue(e),
    });
  };

  const handleOfferId = (e: any) => {
    if (e === null) {
      setOfferIdConnected({ label: '', value: '' });
    } else {
      setOfferIdConnected({ label: e.label, value: e.value });
    }
  };

  const handleCustomerOffice = (e: any) => {
    if (e === null) {
      setCustomerOffice({ label: '', value: '' });
    } else {
      setCustomerOffice({ label: e.label, value: e.value });
    }
  };

  const handlePaymentTerm = (e: any) => {
    if (e === null) {
      setPaymentTerm({ label: '', value: '' });
    } else {
      setPaymentTerm({ label: e.label, value: e.value });
    }
  };

  const handleSignatureUser = (
    updatedSignatureUser: { label: string; value: string } | null
  ) => {
    const emptySignatureUser = {
      label: '',
      value: '',
    };

    setSignatureUser(
      updatedSignatureUser === null ? emptySignatureUser : updatedSignatureUser
    );
  };

  const onChangeDate = (date: any, dateType: string) => {
    if (date !== null && dateType === 'start') {
      setStartDate(date);
    } else if (date !== null && dateType === 'end') {
      setEndDate(date);
    } else if (dateType === 'siteStart') {
      // The dates of the building project can be emptied
      setSiteStartDate(date);
    } else if (dateType === 'siteEnd') {
      setSiteEndDate(date);
    } else if (dateType === 'requestedSubmission') {
      setRequestedSubmissionDate(date);
    }
  };

  const statusSaved = (status: string) => {
    setInitialStatus(status);

    if (status === SENT || status === APPROVED) {
      setLocked(true);
    }

    // A closed opportunity is kept as it was closed; reopened it can be edited again
    if (isClosedOpportunityStatus(status)) {
      setLocked(true);
    } else if (status === OPEN && isClosedOpportunityStatus(initialStatus)) {
      setLocked(false);
    }

    // The offer made of the converted opportunity is shown with a link
    if (isConvertedStatus(status) && offerId !== undefined) {
      apiOffers
        .getOffer(offerId)
        .then((result) => {
          setConvertedOfferId(result.convertedOfferId ?? null);
          dispatch(
            addMessage(t(`${translations.offers}:opportunity.Converted`))
          );
        })
        .catch(() => undefined);
    }
  };

  // A save can fail after the content was saved (e.g. the status change after it), so the
  // edit version is read again; after a conflict the user reloads the page instead
  const refreshEditVersion = (error: any) => {
    if (error?.response?.status === 409 || offerId === undefined) {
      return;
    }

    const request = isAdditionalOffer
      ? apiOffers.additionalOffers.get(offerId)
      : apiOffers.getOffer(offerId);
    request
      .then((loaded: { editVersion?: number }) =>
        setEditVersion(loaded?.editVersion)
      )
      .catch(() => undefined);
  };

  const getStatusErrorMessage = (error: any) => {
    // Another user saved after this page was loaded: nothing was overwritten
    const conflict = getConflictMessage(error, t);
    if (conflict !== null) {
      return conflict;
    }

    const message = error?.response?.data?.message;

    if (
      message === 'Another revision is already approved' ||
      message === 'Sent offer cannot be reopened' ||
      message === 'Offer type cannot be changed' ||
      message === 'Converted opportunity status cannot be changed'
    ) {
      return t(`${translations.offers}:${message}`);
    }

    return t(`${translations.offers}:Offer failed`);
  };

  // The content of a locked (sent or approved) offer cannot be changed, only its status
  const saveLockedOfferStatus = async (status: string) => {
    if (offerId === undefined || status === initialStatus) {
      return;
    }

    try {
      if (type === ADDITIONAL_OFFER) {
        await apiOffers.additionalOffers.patchStatus(offerId, status);
      } else {
        await apiOffers.patchStatus(offerId, status);
      }

      statusSaved(status);
      dispatch(addMessage(t(`${translations.offers}:Offer saved`)));
    } catch (error) {
      dispatch(addMessage(getStatusErrorMessage(error)));
    }
  };

  // keepSavedStatus: e.g. the delivery terms are saved without a status change picked in
  // the basic information (approving creates a project, sending locks the offer). A button
  // may call this with its click event, so only true keeps the status.
  const saveOffer = (keepSavedStatus?: unknown) => {
    if (locked) {
      saveLockedOfferStatus(formState.status);
      return;
    }

    if (formState.target === undefined || formState.target === '') {
      dispatch(addMessage(t(`${translations.common}:Fill all fields`)));
      return;
    }
    const offer: IOfferPost = getOfferPost(
      formState,
      customerOffice,
      // The design office is one of the contacts of the project
      undefined,
      signatureUser === undefined || signatureUser.value === ''
        ? null
        : signatureUser.value,
      paymentTerm,
      startDate,
      endDate,
      // The type can be corrected in the basic information, e.g. offer -> preliminary offer
      formState.offerType || getOfferType(typeId, type),
      additionalRecipients
        .filter((row) => row.office !== null && row.office.value !== '')
        .map((row) => ({
          contactId: parseInt(row.office?.value ?? '', 10),
          contactPersonId:
            row.personId === '' ? undefined : parseInt(row.personId, 10),
        }))
    );
    offer.projectContacts = getProjectContactsPost(projectContactRows);
    offer.siteStarts = getISODate(siteStartDate) ?? null;
    offer.requestedSubmissionDate = getISODate(requestedSubmissionDate) ?? null;
    // The estimated sums of an opportunity (other offers keep theirs, i.e. none)
    if (offer.type === OPPORTUNITY) {
      offer.estimates = getEstimatesPost(estimateRows);
    }
    offer.siteEnds = getISODate(siteEndDate) ?? null;
    if (keepSavedStatus === true && initialStatus) {
      offer.status = initialStatus;
    }
    let result;

    if (!offerSubmitted && id === undefined) {
      // A second click while saving would create the offer twice
      if (saving.current) {
        return;
      }

      saving.current = true;
      // Create new offer
      const submit = async () => {
        try {
          result = await apiOffers.post(offer);
          setSavedId(result.id);
          setOfferSubmitted(true);
          setOfferIsOpen(false); // Close basic info section
          dispatch(addMessage(t(`${translations.offers}:Offer saved`)));
          history.push(`/offers/${result.id}/${offer.type}`);
        } catch (error) {
          dispatch(addMessage(t(`${translations.offers}:Offer failed`)));
        } finally {
          saving.current = false;
        }
      };
      submit().catch((e) => console.log(e));
    } else if (offerSubmitted) {
      // A second click while saving would send the same version again and be refused
      if (saving.current) {
        return;
      }

      saving.current = true;
      const submit = async () => {
        try {
          result = await apiOffers.put({ ...offer, editVersion }, offerId);
          // The save made the next version
          setEditVersion(
            editVersion === undefined ? undefined : editVersion + 1
          );
          statusSaved(offer.status);
          dispatch(addMessage(t(`${translations.offers}:Offer saved`)));

          // The page address contains the type, reload the page with the new type
          if (offer.type !== type) {
            history.push(`/offers/${offerId}/${offer.type}`);
          }
        } catch (error) {
          dispatch(addMessage(getStatusErrorMessage(error)));
          refreshEditVersion(error);
        } finally {
          saving.current = false;
        }
      };
      submit().catch((e) => console.log(e));
    }
  };

  const copyOffer = () => {
    const saveCopiedOffer = async () => {
      try {
        const result = await apiOffers.postCopyOffer(offerId);
        setSavedId(result.id);
        setOfferSubmitted(true);
        setOfferIsOpen(false);
        dispatch(addMessage(t(`${translations.offers}:Offer copied`)));
        history.push('/offers');
      } catch (error) {
        console.log(error);
        dispatch(addMessage(t(`${translations.offers}:Offer failed`)));
      }
    };
    saveCopiedOffer().catch((e) => console.log(e));
  };

  const copyAddionalOffer = () => {
    if (
      offerIdConnected === undefined ||
      offerIdConnected.value === undefined ||
      offerIdConnected.value === ''
    ) {
      dispatch(addMessage(t(`${translations.offers}:Choose offer`)));
      return;
    }

    const saveCopiedAdditionalOffer = async () => {
      try {
        const result = await apiOffers.additionalOffers.postCopyAdditionalOffer(
          offerId
        );
        setSavedId(result.id);
        setOfferSubmitted(true);
        setOfferIsOpen(false); // Close basic info section
        dispatch(addMessage(t(`${translations.offers}:Offer copied`)));
        history.push('/offers');
      } catch (error) {
        console.log(error);
        dispatch(addMessage(t(`${translations.offers}:Offer failed`)));
      }
    };
    saveCopiedAdditionalOffer().catch((e) => console.log(e));
  };

  // keepSavedStatus as in saveOffer
  const saveAdditionalOffer = (keepSavedStatus?: unknown) => {
    if (locked) {
      saveLockedOfferStatus(additionalOfferFormState.status);
      return;
    }

    if (
      offerIdConnected === undefined ||
      offerIdConnected.value === undefined ||
      offerIdConnected.value === ''
    ) {
      dispatch(addMessage(t(`${translations.offers}:Choose offer`)));
      return;
    }
    const offer: IAdditionalOfferPost = {
      description: additionalOfferFormState.description,
      offerId: parseInt(offerIdConnected.value, 10),
      offerText: additionalOfferFormState.offerText,
      deliveryTerms: additionalOfferFormState.deliveryTerms,
      signatureUserId:
        signatureUser === undefined || signatureUser.value === ''
          ? null
          : signatureUser.value,
      status:
        keepSavedStatus === true && initialStatus
          ? initialStatus
          : additionalOfferFormState.status,
    };
    let result;

    if (!offerSubmitted && id === undefined) {
      // A second click while saving would create the additional offer twice
      if (saving.current) {
        return;
      }

      saving.current = true;
      // Create new offer
      const submit = async () => {
        try {
          result = await apiOffers.additionalOffers.post(offer);
          setSavedId(result.id);
          setOfferSubmitted(true);
          setOfferIsOpen(false); // Close basic info section
          dispatch(addMessage(t(`${translations.offers}:Offer saved`)));
          history.push(`/offers/${result.id}/additional-offer`);
        } catch (error) {
          dispatch(addMessage(t(`${translations.offers}:Offer failed`)));
        } finally {
          saving.current = false;
        }
      };
      submit().catch((e) => console.log(e));
    } else if (offerSubmitted) {
      if (saving.current) {
        return;
      }

      saving.current = true;
      const submit = async () => {
        try {
          result = await apiOffers.additionalOffers.put(
            { ...offer, editVersion },
            offerId
          );
          // The save made the next version
          setEditVersion(
            editVersion === undefined ? undefined : editVersion + 1
          );
          statusSaved(offer.status);
          dispatch(addMessage(t(`${translations.offers}:Offer saved`)));
        } catch (error) {
          dispatch(addMessage(getStatusErrorMessage(error)));
          refreshEditVersion(error);
        } finally {
          saving.current = false;
        }
      };
      submit().catch((e) => console.log(e));
    }
  };

  const searchOfferId = async (inputValue: string) =>
    getOffersDropdown(
      await apiOffers.get({
        limit: 100,
        offset: 0,
        search: inputValue,
        type: 'offer',
      })
    );

  const searchCustomerOffice = async (inputValue: string) =>
    getCustomerDropdown(
      await customerApi.get({
        limit: 100,
        offset: 0,
        ownOrganizationUnit: true,
        search: inputValue,
        type: 'customer',
      })
    );

  // A contact of the project can be any company in the contacts (any type)
  const searchProjectContactOffice = async (inputValue: string) =>
    getCustomerDropdown(
      await customerApi.get({
        limit: 100,
        offset: 0,
        ownOrganizationUnit: true,
        search: inputValue,
      })
    );

  const canDeleteOffer =
    currentUser?.permissions?.some(
      (permission) => permission.type === DELETE_OFFERS
    ) ?? false;

  const deleteOffer = async () => {
    if (offerId === undefined || deleting) {
      return;
    }

    setModalDeleteConfirmation(false);
    setDeleting(true);

    try {
      if (type === ADDITIONAL_OFFER) {
        await apiOffers.additionalOffers.delete(offerId);
      } else {
        await apiOffers.delete(offerId);
      }

      dispatch(addMessage(t(`${translations.offers}:Offer deleted`)));
      history.push('/offers');
    } catch (error) {
      const hasAdditionalOffers =
        error?.response?.data?.message === 'Offer has additional offers';

      dispatch(
        addMessage(
          t(
            `${translations.offers}:${
              hasAdditionalOffers
                ? 'Offer has additional offers'
                : 'Offer delete failed'
            }`
          )
        )
      );
      setDeleting(false);
    }
  };

  const goPreviousPage = () => {
    history.push('/offers');
  };

  const isAdditionalOffer = type === ADDITIONAL_OFFER || typeId === '2';

  // A sales opportunity has no calculation, PDF, price list or delivery terms
  const isOpportunity =
    !isAdditionalOffer &&
    (formState.offerType || getOfferType(typeId, type)) === OPPORTUNITY;

  // The other users on this offer or its positions now
  const visitedId = offerSubmitted ? offerId : undefined;
  const visitors = useOfferVisit(
    isAdditionalOffer
      ? { additionalOfferId: visitedId }
      : { offerId: visitedId },
    false
  );

  return (
    <PageContainer className="offer-page">
      <ModalConfirm
        showing={modalDeleteConfirmation}
        setShowing={() => setModalDeleteConfirmation(!modalDeleteConfirmation)}
        header={t(`${translations.offers}:Delete offer`)}
        text={t(`${translations.offers}:Delete offer confirmation`)}
        yesButton={t(`${translations.common}:Confirm`)}
        noButton={t(`${translations.common}:Cancel`)}
        confirm={deleteOffer}
      />
      {nda !== null ? (
        <OfferNdaModal
          isOpen={ndaIsOpen}
          nda={nda}
          ndaOfferId={ndaOfferId}
          onSaved={setNda}
          readOnly={readOnly}
          toggle={() => setNdaIsOpen(!ndaIsOpen)}
        />
      ) : null}
      <BasicFormContainer>
        <OfferVisitors
          isMaker={makerId !== undefined && makerId === currentUser?.id}
          others={visitors}
        />
        {readOnly ? (
          <div className="offer-read-only-info" role="status">
            {t(`${translations.offers}:readOnly.Info`)}
          </div>
        ) : null}
        {(nda !== null || canReadHistory) && offerId !== undefined ? (
          <div className="offer-nda-bar">
            {canReadHistory ? (
              <PrimaryButton onClick={() => setHistoryIsOpen(true)}>
                {t(`${translations.offers}:history.Button`).toLocaleUpperCase()}
              </PrimaryButton>
            ) : null}
            {nda !== null && nda.protections.length > 0 ? (
              <span
                className={`offer-nda-status ${
                  ndaRestricted ? 'restricted' : 'active'
                }`}
              >
                {t(
                  `${translations.offers}:${
                    ndaRestricted ? 'nda.Restricted' : 'nda.Active'
                  }`
                )}
              </span>
            ) : null}
            {nda !== null ? (
              <PrimaryButton onClick={() => setNdaIsOpen(true)}>
                NDA
              </PrimaryButton>
            ) : null}
          </div>
        ) : null}
        {canReadHistory && offerId !== undefined ? (
          <OfferHistoryModal
            isAdditionalOffer={isAdditionalOffer}
            isOpen={historyIsOpen}
            offerId={offerId}
            toggle={() => setHistoryIsOpen(!historyIsOpen)}
          />
        ) : null}
        {isAdditionalOffer ? (
          <AdditionalOfferBasicInfo
            initialStatus={initialStatus}
            locked={locked}
            readOnly={viewOnly}
            toggleOffer={toggleOffer}
            offerIsOpen={offerIsOpen}
            formState={additionalOfferFormState}
            handleFormState={handleAdditionalOfferFormState}
            saveOffer={saveAdditionalOffer}
            searchOfferId={searchOfferId}
            offerId={offerIdConnected}
            handleOfferId={handleOfferId}
            signatureUser={signatureUser}
            handleSignatureUser={handleSignatureUser}
          />
        ) : (
          <OfferBasicInfo
            initialStatus={initialStatus}
            locked={locked}
            readOnly={viewOnly}
            organizationUnitField={
              // Only the user who can edit the offer can move it to another department
              offerSubmitted && offerId !== undefined && !viewOnly ? (
                <OfferDepartment
                  offerId={offerId}
                  organizationUnit={formState.organizationUnit}
                  onMoved={(organizationUnit) =>
                    setFormState({ ...formState, organizationUnit })
                  }
                />
              ) : null
            }
            typeCanBeChanged={!locked && !readOnly && revisionCount <= 1}
            additionalRecipients={
              (formState.offerType || getOfferType(typeId, type)) ===
                PRELIMINARY_OFFER || additionalRecipients.length > 0 ? (
                <AdditionalRecipients
                  locked={locked || readOnly}
                  rows={additionalRecipients}
                  searchCustomerOffice={searchCustomerOffice}
                  setRows={setAdditionalRecipients}
                />
              ) : null
            }
            toggleOffer={toggleOffer}
            offerIsOpen={offerIsOpen}
            formState={formState}
            handleFormState={handleFormState}
            customerOffice={customerOffice}
            handleCustomerOffice={handleCustomerOffice}
            searchCustomerOffice={searchCustomerOffice}
            startDate={startDate}
            onChangeDate={onChangeDate}
            endDate={endDate}
            siteStartDate={siteStartDate}
            siteEndDate={siteEndDate}
            requestedSubmissionDate={requestedSubmissionDate}
            offerId={type === ADDITIONAL_OFFER ? undefined : offerId}
            projectContacts={
              <ProjectContacts
                locked={locked || readOnly}
                rows={projectContactRows}
                searchOffice={searchProjectContactOffice}
                setRows={setProjectContactRows}
              />
            }
            saveOffer={saveOffer}
            customers={customers}
            paymentTerms={paymentTerms}
            handlePaymentTerm={handlePaymentTerm}
            paymentTerm={paymentTerm}
            signatureUser={signatureUser}
            handleSignatureUser={handleSignatureUser}
          />
        )}

        <hr />
        {/* Once ordered: the projects and D365 numbers from ProBuilder */}
        {workNumbers !== null ? (
          <>
            <OfferWorkNumbers workNumbers={workNumbers} />
            <hr />
          </>
        ) : null}
        {/* The offer made of the opportunity, or the opportunity this offer was made of */}
        {isOpportunity && convertedOfferId !== null ? (
          <p className="opportunity-link">
            {t(`${translations.offers}:opportunity.Converted info`)}{' '}
            <LinkButton
              onClick={() =>
                history.push(
                  `/offers/${convertedOfferId}/${
                    initialStatus === CONVERTED_TO_OFFER
                      ? 'offer'
                      : PRELIMINARY_OFFER
                  }`
                )
              }
            >
              {t(`${translations.offers}:opportunity.Open converted`)}
            </LinkButton>
          </p>
        ) : null}
        {!isOpportunity && opportunityId !== null ? (
          <p className="opportunity-link">
            {t(`${translations.offers}:opportunity.Made of opportunity`)}{' '}
            <LinkButton
              onClick={() =>
                history.push(`/offers/${opportunityId}/${OPPORTUNITY}`)
              }
            >
              {t(`${translations.offers}:opportunity.Open opportunity`)}
            </LinkButton>
          </p>
        ) : null}
        {isOpportunity ? (
          <>
            <OpportunityEstimates
              locked={locked || viewOnly}
              rows={estimateRows}
              // Saved without a status change picked but not saved
              save={offerSubmitted ? () => saveOffer(true) : undefined}
              setRows={setEstimateRows}
            />
            <hr />
          </>
        ) : null}
        {id !== undefined && offerId !== undefined && !isOpportunity ? (
          <>
            <OfferRevisions
              canCreate={!viewOnly}
              offerId={offerId}
              onRevisionsLoaded={setRevisionCount}
              type={getOfferType(typeId, type)}
            />
            <hr />
          </>
        ) : null}
        {isOpportunity ? null : (
          <OfferProducts
            offerId={
              savedId === undefined && id !== undefined
                ? parseInt(id, 10)
                : savedId
            }
            locked={locked || readOnly}
            ndaPositionsRestricted={isNdaRestricted(nda, POSITIONS)}
            offerSubmitted={offerSubmitted}
            productsIsOpen={productsIsOpen}
            toggleProducts={toggleProducts}
            type={getOfferType(typeId, type)}
            products={products}
            setProducts={setProducts}
          />
        )}

        {/* Printed on a page of its own after the offer, before the delivery terms */}
        {offerSubmitted &&
        !isOpportunity &&
        offerId !== undefined &&
        priceListShowQuantities !== null &&
        !isNdaRestricted(nda, POSITIONS) ? (
          <>
            <hr />
            <CollapsibleOfferSection
              isOpen={priceListIsOpen}
              title={t(`${translations.offers}:priceList.Title`)}
              toggle={() => setPriceListIsOpen(!priceListIsOpen)}
            >
              <OfferPriceList
                showQuantities={priceListShowQuantities}
                setShowQuantities={setPriceListShowQuantities}
                locked={locked || readOnly}
                offerId={offerId}
                products={products}
                setProducts={setProducts}
                type={getOfferType(typeId, type)}
              />
            </CollapsibleOfferSection>
          </>
        ) : null}

        {/* Printed on a page of their own at the end of the offer PDF */}
        {offerSubmitted && offerId !== undefined && !isOpportunity ? (
          <>
            <hr />
            <CollapsibleOfferSection
              isOpen={deliveryTermsIsOpen}
              title={t(`${translations.offers}:Delivery terms`)}
              toggle={() => setDeliveryTermsIsOpen(!deliveryTermsIsOpen)}
            >
              <OfferTextField
                label=""
                // Users outside the NDA or with only the right to read cannot save the offer
                locked={locked || viewOnly}
                onChange={(deliveryTerms) => {
                  const event = {
                    target: { name: 'deliveryTerms', value: deliveryTerms },
                  };

                  if (isAdditionalOffer) {
                    handleAdditionalOfferFormState(event);
                  } else {
                    handleFormState(event);
                  }
                }}
                tall
                textType="delivery-terms"
                value={
                  isAdditionalOffer
                    ? additionalOfferFormState.deliveryTerms
                    : formState.deliveryTerms
                }
              />
              <div className="offer-delivery-terms-actions">
                <PrimaryButton
                  className="button-one-line"
                  disabled={locked || viewOnly}
                  // Saves the offer without a status change picked but not saved
                  onClick={() =>
                    isAdditionalOffer
                      ? saveAdditionalOffer(true)
                      : saveOffer(true)
                  }
                >
                  {t(
                    `${translations.offers}:Save delivery terms`
                  ).toLocaleUpperCase()}
                </PrimaryButton>
              </div>
            </CollapsibleOfferSection>
            {/* Pages of their own after the delivery terms: a text, an image or a PDF */}
            <OfferExtraPages
              locked={locked || viewOnly}
              owner={isAdditionalOffer ? 'additionaloffers' : 'offers'}
              ownerId={offerId}
            />
          </>
        ) : null}

        {memo !== null && offerId !== undefined && !ndaRestricted ? (
          <>
            <hr />
            <OfferMemo
              initialMemo={memo}
              offerId={offerId}
              readOnly={readOnly}
              type={getOfferType(typeId, type)}
            />
          </>
        ) : null}

        {/* At the left edge, away from the other buttons */}
        {canDeleteOffer && (
          <PrimaryButton
            disabled={offerId === undefined || locked || readOnly || deleting}
            float={LEFT}
            onClick={() => setModalDeleteConfirmation(true)}
          >
            {t('common:Delete').toLocaleUpperCase()}
          </PrimaryButton>
        )}
        <PrimaryButton float={LEFT} onClick={goPreviousPage}>
          {t('common:Back').toLocaleUpperCase()}
        </PrimaryButton>
        {/* An opportunity has no offer PDF */}
        {isOpportunity ? null : (
          <>
            <PrimaryButton
              disabled={id === undefined || id === null || ndaRestricted}
              float={RIGHT}
              onClick={() => openOfferPdf(offerId || -1, type)}
            >
              {t('offers:Open PDF').toLocaleUpperCase()}
            </PrimaryButton>
            <PrimaryButton
              disabled={offerId === undefined || ndaRestricted}
              float={RIGHT}
              onClick={() => setPreviewIsOpen(!previewIsOpen)}
            >
              {t('offers:pdfPreview.Open').toLocaleUpperCase()}
            </PrimaryButton>
          </>
        )}
        <PrimaryButton
          disabled={viewOnly}
          float={RIGHT}
          onClick={type === ADDITIONAL_OFFER ? copyAddionalOffer : copyOffer}
        >
          {t('offers:Copy Offer').toLocaleUpperCase()}
        </PrimaryButton>
      </BasicFormContainer>
      {previewIsOpen && offerId !== undefined ? (
        <PdfPreviewPanel
          locked={locked || readOnly}
          offerId={offerId}
          onClose={() => setPreviewIsOpen(false)}
          type={getOfferType(typeId, type)}
        />
      ) : null}
    </PageContainer>
  );
};

export default Offer;
